// Copyright ©️ 2026 eVolve MEP, LLC

import type { Ref } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { ActionIcon, Group, Divider } from '@mantine/core';

type Props = {
  onClick: () => void;
  disabled?: boolean;
  direction: 'increase' | 'decrease';
  ref?: Ref<HTMLDivElement>;
};

const StepButtonActionIcon = ({ direction, ...props }: StrongOmit<Props, 'ref'>) => (
  <ActionIcon
    variant="subtle"
    color="gray"
    radius={0}
    w="100%"
    h="100%"
    fz="var(--input-fz)"
    aria-label={direction}
    // Keeps focus in the field, so a tap doesn't also blur it and trigger a second validation.
    onMouseDown={(event) => event.preventDefault()}
    {...props}
  >
    {direction === 'increase' ? '+' : '-'}
  </ActionIcon>
);

/** A button meant to be placed in the right or left sections of an input field for incrementing/decrementing */
export const StepButton = ({ ref, ...props }: Props) => (
  <Group h="100%" w="var(--input-height)" gap={0} style={{ flex: '0 0 auto' }} wrap="nowrap" ref={ref}>
    {props.direction === 'increase' ? (
      <>
        <Divider orientation="vertical" />
        <StepButtonActionIcon {...props} />
      </>
    ) : (
      <>
        <StepButtonActionIcon {...props} />
        <Divider orientation="vertical" />
      </>
    )}
  </Group>
);
